<script setup>
/**
 * BS Modal
 */
import { Modal } from 'bootstrap'
import { onMounted, onUnmounted, ref, shallowRef, watchEffect } from 'vue'
import { useModalStack } from '@/stores/modalstack'
import { storeToRefs } from 'pinia'

const emit = defineEmits(['hidden'])
const modal = ref(null)
const props = defineProps({
  size: String,
  header: {
    type: Boolean,
    default: true,
  },
  classes: [Array, Object, String, null],
  close: {
    type: Boolean,
    default: true,
  },
  className: [String, null],
  useHistory: {
    type: Boolean,
    default: true,
  },
})
let mbs = ref(null)

const modalStack = useModalStack()
const { stack } = storeToRefs(modalStack)

let pushed = false
let closingByPop = false
let ignorePop = false

function restoreBodyScroll() {
  // Only restore body scroll if there are no other modals open
  // Check both modal stack and Bootstrap's modal backdrop
  const hasOtherModals = stack.value.length > 0
  const hasBackdrop = document.querySelector('.modal-backdrop.show')
  const hasOpenModals = document.querySelector('.modal.show')

  if (!hasOtherModals && !hasBackdrop && !hasOpenModals) {
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      document.body.classList.remove('modal-open')
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    })
  }
}

function onPopState() {
  if (ignorePop) {
    ignorePop = false
    return
  }

  if (stack.value.length > 0) {
    closingByPop = true
    modalStack.pop(null)
  }
}

onMounted(() => {
  mbs.value = new Modal(modal.value)
  modal.value.addEventListener('hidden.bs.modal', onHidden)
  modal.value.addEventListener('hide.bs.modal', onHide)
  mbs.value.show()

  if (props.useHistory) {
    try {
      history.pushState({ modal: true }, '')
      pushed = true
    } catch {
      pushed = false
    }
  }

  if (props.useHistory) {
    window.addEventListener('popstate', onPopState)
  }
})

onUnmounted(() => {
  // Clean up event listener first
  if (props.useHistory) {
    window.removeEventListener('popstate', onPopState)
  }

  if (modal.value) {
    modal.value.removeEventListener('hidden.bs.modal', onHidden)
    modal.value.removeEventListener('hide.bs.modal', onHide)
  }

  // Dispose Bootstrap modal instance
  if (mbs.value) {
    // Hide modal if still shown before disposing
    if (modal.value && modal.value.classList.contains('show')) {
      try {
        mbs.value.hide()
      } catch (e) {
        console.warn('Error hiding modal on unmount:', e)
      }
    }
    mbs.value.dispose()
    mbs.value = undefined
  }

  // Restore body scroll after cleanup
  restoreBodyScroll()
})

defineExpose({ hide: () => mbs.value && mbs.value.hide() })

function onHide() {
  document.activeElement.blur()
}

function onHidden() {
  emit('hidden')
  restoreBodyScroll()

  if (!props.useHistory || !pushed) return

  if (closingByPop) {
    closingByPop = false
    pushed = false
    return
  }
  try {
    ignorePop = true
    history.back()
  } catch {
    // ignore
  } finally {
    pushed = false
  }
}

const clDialog = shallowRef({})
watchEffect(() => {
  clDialog.value = { ...clDialog.value, [props.className]: props.className }
  if (props.size == 'sm') {
    clDialog.value = { ...clDialog.value, 'modal-sm': true }
  }
  if (props.size == 'fit-content') {
    clDialog.value = { ...clDialog.value, 'fit-content': true }
  }
  if (props.size == 'full') {
    clDialog.value = { ...clDialog.value, 'modal-fullscreen': true }
  }
})
</script>
<template>
  <Teleport to="body">
    <div
      class="modal fade"
      :class="classes"
      tabindex="-1"
      ref="modal"
    >
      <div
        class="modal-dialog"
        :class="clDialog"
      >
        <div class="modal-content">
          <div
            class="modal-header"
            v-if="header"
          >
            <slot name="header" />
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="mbs.hide()"
              v-if="close"
            />
          </div>
          <div class="modal-body">
            <slot
              name="default"
              :mbs="mbs"
            />
          </div>
          <div
            class="modal-body"
            v-if="$slots.secondbody"
          >
            <slot name="secondbody" />
          </div>
          <div
            class="modal-footer"
            v-if="$slots.footer"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
